import { Money } from '@ndvr/customer-api';
import { CurrencyCode } from '@ndvr/portfolio-api';

/*
 *
 * Number formatting util functions
 *
 */

function shortenNumberWithUnit(number: number | string): NumberWithUnit {
    if (typeof number === 'string') {
        return shortenNumberWithUnit(parseFloat(number));
    }

    // trillion
    if (Math.abs(number) >= 1_000_000_000_000) {
        return {
            number: number / Math.pow(10, 11) / 10,
            unit: 'T',
        };
    }
    // billion
    if (Math.abs(number) >= 1_000_000_000) {
        return {
            number: number / Math.pow(10, 8) / 10,
            unit: 'B',
        };
    }
    // million
    if (Math.abs(number) >= 1_000_000) {
        return {
            number: number / Math.pow(10, 5) / 10,
            unit: 'M',
        };
    }
    // thousand
    if (Math.abs(number) >= 1000) {
        return {
            number: number / Math.pow(10, 2) / 10,
            unit: 'K',
        };
    }
    return {
        number,
        unit: '',
    };
}

function numDigits(x: number) {
    return (Math.log10((x ^ (x >> 31)) - (x >> 31)) | 0) + 1;
}

export const formatNumber = (number: number | string | undefined | null, fractionDigits = 0): string => {
    if (number === undefined || number === null || number === '') {
        return formatNumber(0, fractionDigits);
    }
    if (typeof number === 'string') {
        return formatNumber(parseFloat(number), fractionDigits);
    }
    return number.toLocaleString('en-US', { maximumFractionDigits: fractionDigits, minimumFractionDigits: 0 });
};

/**
 * Dynamically configures the fraction digits to have around 3 digits altogether.
 *
 * @return human readable format with unit appended
 */
export const formatNumberHuman = (number: number | string): string => {
    if (typeof number === 'string') {
        return formatNumberHuman(parseFloat(number));
    }
    const result = shortenNumberWithUnit(number);
    const integerDigits = numDigits(result.number);
    const maximumFractionDigits = 3 - integerDigits; // 3 is the total number of digits

    const formatted = Math.abs(result.number).toLocaleString('en-US', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: maximumFractionDigits,
    });
    return Math.sign(number) === -1 ? `-${formatted}${result.unit}` : `${formatted}${result.unit}`;
};

/**
 * Rounds the number to a human understandable number + 2 fraction digits.
 * E.g.:
 *  136 543 -> 136,52K
 *  136 543 123 -> 136,25M
 *  136 543 123 987 -> 136,35B
 *
 * @return short format with unit appended
 */
export const formatNumberShort = (number: number | string, fractionDigits = 2): string => {
    const result = shortenNumberWithUnit(number);
    const formatted = result.number.toLocaleString('en-US', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: fractionDigits,
    });
    return `${formatted}${result.unit}`;
};

export const formatCurrency = (
    number: number | string | undefined | null,
    currency: string = CurrencyCode.USD,
    fractionDigits = 0,
): string => {
    if (number === undefined || number === null || number === '') {
        return '-';
    }
    if (typeof number === 'string') {
        return formatCurrency(parseFloat(number), currency, fractionDigits);
    }

    const formatted = Math.abs(number).toLocaleString('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: fractionDigits,
    });
    return Math.sign(number) === -1 ? `( ${formatted} )` : formatted;
};

export const formatMoney = (money: Money | undefined | null, fractionDigits = 0): string => {
    if (money === undefined || money === null) {
        return '-';
    }
    return formatCurrency(money.amount, money.currency, fractionDigits);
};

export const formatCurrencyHuman = (number: number | string | undefined | null, currency: string): string => {
    if (number === undefined || number === null || number === '') {
        return '-';
    }
    if (typeof number === 'string') {
        return formatCurrencyHuman(parseFloat(number), currency);
    }

    const result = shortenNumberWithUnit(number);
    const integerDigits = numDigits(result.number);
    const maximumFractionDigits = 3 - integerDigits; // 3 is the total number of digits

    const formatted = Math.abs(result.number).toLocaleString('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: maximumFractionDigits,
    });
    return Math.sign(number) === -1 ? `( ${formatted}${result.unit} )` : `${formatted}${result.unit}`;
};

export const formatCurrencyShort = (
    number: number | string | undefined | null,
    currency: string = CurrencyCode.USD,
    fractionDigits = 2,
): string => {
    if (number === undefined || number === null || number === '') {
        return '-';
    }
    if (typeof number === 'string') {
        return formatCurrencyShort(parseFloat(number), currency, fractionDigits);
    }
    const result = shortenNumberWithUnit(number);
    const formatted = Math.abs(result.number).toLocaleString('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: fractionDigits,
    });
    return Math.sign(number) === -1 ? `( ${formatted}${result.unit} )` : `${formatted}${result.unit}`;
};

export const formatPercent = (number: number | string | null | undefined, fractionDigits = 2): string => {
    if (number === null || number === undefined || number === '') {
        return '-%';
    }
    return `${formatNumber(number, fractionDigits)}%`;
};

/**
 * 1_160_000 -> 1_200_000
 * 11_123_000 -> 11_000_000
 */
export const roundNumberHuman = (number: number): number => {
    const len = Math.ceil(Math.log10(number + 1));
    return Math.round(number / Math.pow(10, len - 2)) * Math.pow(10, len - 2);
};

export const asHighlight = (number?: number | string | null): 'positive' | 'negative' | undefined => {
    if (number === undefined || number === null || isNaN(Number(number))) {
        return undefined;
    }
    return Number(number) >= 0 ? 'positive' : 'negative';
};

interface NumberWithUnit {
    number: number;
    unit: string;
}
